import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { StudentNameDialog } from "@/components/StudentNameDialog";
import { BookOpen, Books, CheckCircle, VideoCamera, EnvelopeSimple, Certificate as CertIcon, PencilSimple, Student } from "@phosphor-icons/react";

export default function Library() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nameDialog, setNameDialog] = useState(false);

  useEffect(() => {
    api.get("/library").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user && (!user.learners || user.learners.length === 0)) setNameDialog(true);
  }, [user]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="library-page">
      <StudentNameDialog open={nameDialog} onOpenChange={setNameDialog} />
      <div className="mb-10">
        <span className="font-body font-semibold text-brand-coral uppercase tracking-[0.2em] text-xs">Hi {user?.name?.split(" ")[0]} 👋</span>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl text-ink tracking-tight mt-1">My Bookshelf</h1>
        <p className="font-body text-ink-soft mt-3 text-lg">Every package you own, ready to read anytime.</p>
        <button
          onClick={() => setNameDialog(true)}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-sky/10 hover:bg-brand-sky/20 text-ink font-body font-semibold text-sm px-4 py-2 transition-colors"
          data-testid="edit-student-name-btn"
        >
          <Student size={18} weight="duotone" className="text-brand-sky" />
          {user?.student_name
            ? <>Certificates for <span className="font-bold">{user.student_name}</span></>
            : <>Set the learner's name for certificates</>}
          <PencilSimple size={15} weight="bold" className="text-ink-soft" />
        </button>
      </div>

      {loading ? (
        <p className="font-body text-ink-soft">Loading your shelf…</p>
      ) : items.length === 0 ? (
        <div className="bg-white sticker rounded-3xl p-12 text-center" data-testid="library-empty">
          <div className="grid place-items-center w-20 h-20 mx-auto rounded-3xl bg-brand-sky/10 mb-6">
            <Books size={40} weight="duotone" className="text-brand-sky" />
          </div>
          <h2 className="font-heading font-bold text-2xl text-ink">Your shelf is empty</h2>
          <p className="font-body text-ink-soft mt-2">Pick a reading package to get started.</p>
          <Link to="/catalog"><Button className="mt-6 rounded-full bg-brand-sky hover:bg-brand-skylight text-white font-semibold px-8 py-6 text-lg">Browse packages</Button></Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
              className="bg-white sticker rounded-3xl overflow-hidden flex flex-col" data-testid={`library-item-${p.slug}`}>
              <div className="h-40 overflow-hidden relative">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                {p.progress === 100 && (
                  <span className="absolute top-3 right-3 flex items-center gap-1 px-3 py-1 rounded-full bg-brand-grass text-white text-xs font-bold">
                    <CheckCircle size={14} weight="fill" /> Done!
                  </span>
                )}
              </div>
              <div className="p-6 flex flex-col flex-1">
                <p className="font-body text-xs uppercase tracking-wider" style={{ color: p.color }}>{p.level}</p>
                <h3 className="font-heading font-bold text-xl text-ink mt-1">{p.title}</h3>
                {p.kind === "tutoring" ? (
                  <>
                    <p className="font-body text-sm text-ink-soft mt-3 mb-4 leading-relaxed flex-1">
                      {p.booking?.label
                        ? <>Consultation requested for <span className="font-semibold text-ink">{p.booking.label}</span>.</>
                        : "Your session request is in."} We'll email you to confirm the details — nothing is final until we talk.
                    </p>
                    <div className="mt-auto flex items-center gap-2 rounded-full bg-brand-grape/10 text-brand-grape font-bold py-4 px-5 justify-center" data-testid={`booked-${p.slug}`}>
                      <EnvelopeSimple size={20} weight="duotone" /> Consultation pending
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mt-4 mb-2 flex justify-between font-body text-sm text-ink-soft">
                      <span>{p.completed_count}/{p.lesson_count} lessons</span>
                      <span className="font-semibold">{p.progress}%</span>
                    </div>
                    <Progress value={p.progress} className="h-2.5 mb-5" />
                    {p.progress === 100 ? (
                      <div className="mt-auto space-y-2">
                        <Link to={`/certificate/${p.slug}`} data-testid={`certificate-${p.slug}`}>
                          <Button className="w-full rounded-full bg-brand-sun hover:bg-brand-sunlight text-ink font-bold py-5 transition-transform hover:-translate-y-0.5">
                            <CertIcon size={20} weight="duotone" className="mr-2" /> View certificate
                          </Button>
                        </Link>
                        <Link to={`/read/${p.slug}`} data-testid={`read-${p.slug}`}>
                          <Button variant="outline" className="w-full rounded-full border-2 font-semibold py-5">
                            <BookOpen size={18} weight="duotone" className="mr-2" /> Read again
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      <Link to={`/read/${p.slug}`} className="mt-auto" data-testid={`read-${p.slug}`}>
                        <Button className="w-full rounded-full text-white font-bold py-5 transition-transform hover:-translate-y-0.5" style={{ backgroundColor: p.color }}>
                          <BookOpen size={20} weight="duotone" className="mr-2" /> {p.progress > 0 ? "Continue" : "Start"} reading
                        </Button>
                      </Link>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
